// HOOKS
import useSelected from "../../hooks/other/useSelected.other.hook";
import useColumns from "../../hooks/other/useColumns.other.hook";
import useSelectAll from "../../hooks/other/useSelectAll.other.hook";
import { useContext, useEffect } from "react";
// CONTEXTS
import { AssetsContext } from "../../contexts/assets/assets.context";
// COMPONENTS
import QueryContainer from "../../components/tables/query/queryContainer.table.component";
import PaginationBar from "../../components/tables/customizableTable/features/pagination/paginationBar";
import Refresh from "../../components/refresh/refresh.component";
import Spinner from "../../components/spinner/spinner.component";
import { assetColumns } from "../../assets/variables/columns/asset.columns";
import Table from "../../components/tables/customizableTable/table.customizableTable";
import GetAssetFormProps from "../../components/formProps/assets/asset.formProp";
import AssetActionBar from "../../components/actionBar/assets/asset.actionBar";
import AssetInfoBar from "../../components/infoBar/assets/asset.infoBar";

const CLASSNAMES = {
  pagination: {
    arrow:
      "active:shadow-inner active:shadow-black bg-lightPrimary hover:bg-lightSecondary shadow shadow-black hover:cursor-pointer hover:dark:bg-darkSecondary dark:bg-darkPrimary dark:fill-white rounded-md w-8 h-8",
    currentPage:
      "shadow-inner shadow-black dark:bg-darkSecondary bg-lightSecondary dark:text-white text-white hover:cursor-pointer rounded h-8 w-8 flex items-center justify-center",
    page: "active:shadow-inner active:shadow-black shadow shadow-black hover:dark:bg-darkSecondary hover:bg-lightSecondary active:shadow-inner dark:text-white text-white dark:bg-darkPrimary bg-lightPrimary hover:cursor-pointer rounded h-8 w-8 flex items-center justify-center",
  },
  columnFilter: {
    dropdownButton: {
      dropdown:
        "border-animate rounded h-[250px] w-[250px] overflow-y-scroll absolute backdrop-blur-sm bg-greenGlass flex flex-col gap-1 p-2 top-11 z-50",
    },
  },
  limit: {
    dropdownButton: {
      dropdown:
        "border-animate rounded absolute backdrop-blur-sm bg-greenGlass flex flex-col gap-1 p-2 top-11 z-50",
    },
  },
  spinner: {
    container: "flex w-full h-full justify-center items-center",
    spinner:
      "shadow animate-spin border-dashed rounded-full h-16 w-16 border-2 border-primary",
  },
  statusFilter: {
    dropdownButton: {
      dropdown:
        "w-[225px] border-animate rounded overflow-y-auto absolute backdrop-blur-sm bg-greenGlass flex flex-col gap-1 p-2 top-11 z-50",
    },
  },
};
const AssetsPage = () => {
  const { selected, setSelected, removeSelected, resetSelected, addSelected } =
    useSelected();
  const { selectAll, handleSelectAll, resetSelectAll } = useSelectAll();
  const { columns, setColumns, handleColumnFilterCheck, handleResize } =
    useColumns(assetColumns); // Set default columns
  const {
    assets,
    metaData,
    getAssets,
    query,
    handleLimitChange,
    handlePageChange,
    handleSearch,
    handleSort,
    isLoading,
    handleChange,
  } = useContext(AssetsContext);
  const { limit, page, search, sort } = query;

  // Fetch assets on page load
  useEffect(() => {
    getAssets();
  }, []);
  // Reset selectAll when selected changes
  useEffect(() => {
    if (selected.length === 0) resetSelectAll();
  }, [selected]);
  // Reset selectAll when page changes
  useEffect(() => {
    resetSelectAll();
  }, [page]);

  // Add/Remove assets from selected
  const selectAllHandler = () => {
    const updatedSelectedAssets = handleSelectAll(selected, assets, "_id");
    setSelected(updatedSelectedAssets);
  };

  const handleSelect = (event) => {
    const { _id } = event.target.dataset;
    const existingSelection = selected?.find((item) => item._id === _id);
    if (existingSelection) {
      removeSelected(_id);
    } else {
      addSelected(assets.find((item) => item._id === _id));
    }
  };

  const changeHandlers = {
    selectAllHandler,
    handleSelect,
    handleChange: handleChange,
    handleMultiSelectChange: () => {},
  };

  return (
    <div
      style={{ height: "calc(100vh - 136px)" }}
      className="p-4 flex flex-col gap-2 overflow-y-hidden"
    >
      <h1 className="font-ubuntuBold text-lg items-center text-center">
        Assets
      </h1>
      <div className="flex flex-row justify-between">
        <QueryContainer
          queryProps={{
            search: {
              handleChange: handleSearch,
            },
            limit: {
              handleChange: handleLimitChange,
              limit,
            },
            columnFilter: {
              handleChange: handleColumnFilterCheck,
              columns,
            },
          }}
          classNames={{
            limit: CLASSNAMES.limit,
            columnFilter: CLASSNAMES.columnFilter,
          }}
        ></QueryContainer>
        <AssetActionBar />
      </div>
      <div className="p-2">
        <PaginationBar
          metaData={metaData}
          currentPage={page}
          totalPages={metaData?.totalPages || 1}
          handleChange={handlePageChange}
          classNames={CLASSNAMES.pagination}
        />
      </div>
      <div className="flex flex-row justify-between">
        <AssetInfoBar
          metaData={metaData}
          selectedTagProps={{
            label: `Assets: ${selected.length || 0}`,
            handleClear: resetSelected,
          }}
        />
        <Refresh minimumValue={30} handleEvent={getAssets} />
      </div>
      {isLoading || !assets?.length > 0 ? (
        <div className="w-full h-full flex justify-center items-center">
          <Spinner classNames={CLASSNAMES.spinner} />
        </div>
      ) : (
        <div className="flex items-center justify-start overflow-hidden w-full h-full">
          <div className="overflow-x-auto h-full px-1 pb-8">
            <Table
              metaData={metaData}
              columnProps={{ columns, handleResize }}
              sortProps={{ sort, handleSort }}
              options={{ select: true }}
              changeHandlers={changeHandlers}
              getFormProps={GetAssetFormProps}
              defaultColumns={assetColumns}
              data={assets}
              select={true}
              selectAll={selectAll}
              selectedItems={selected}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AssetsPage;
